import React, { useState } from "react";
import { TokenGuard } from "../App";

export const dashboardStyles = `
  :root {
    --bg-dark: #0b0f1a;
    --sidebar-bg: #111827;
    --card-bg: #1f2937;
    --primary: #6366f1;
    --primary-hover: #4f46e5;
    --text-main: #f9fafb;
    --text-muted: #9ca3af;
    --border: #374151;
    --danger: #ef4444;
    --success: #10b981;
    --warning: #f59e0b;
    --sidebar-width: 260px;
    --radius-lg: 16px;
    --radius-md: 12px;
  }

  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { background: var(--bg-dark); color: var(--text-main); font-family: 'Inter', sans-serif; -webkit-font-smoothing: antialiased; }

  .dashboard-layout { display: flex; min-height: 100vh; }

  /* Sidebar */
  .sidebar {
    width: var(--sidebar-width); 
    background: var(--sidebar-bg); 
    border-right: 1px solid var(--border);
    display: flex; 
    flex-direction: column; 
    padding: 2rem 1rem; 
    position: fixed; 
    top: 0;
    bottom: 0; 
    left: 0;
    transition: transform 0.3s ease; 
    z-index: 100;
    overflow-y: auto; 
  }

  /* Mobile Logic */
  @media (max-width: 1024px) {
    .sidebar { transform: translateX(-100%); width: 260px; }
    .sidebar.open { transform: translateX(0); box-shadow: 10px 0 30px rgba(0,0,0,0.5); }
    .main-content { margin-left: 0 !important; padding: 1.5rem !important; margin-top: 60px; }
    .mobile-header { display: flex !important; }
  }

  .mobile-header {
    display: none; position: fixed; top: 0; left: 0; right: 0; height: 60px;
    background: var(--sidebar-bg); border-bottom: 1px solid var(--border);
    padding: 0 1.25rem; align-items: center; justify-content: space-between; z-index: 90;
  }

  .main-content { margin-left: var(--sidebar-width); flex: 1; padding: 3rem 4rem; transition: margin 0.3s ease; }

  /* Grid & UI Containers */
  .view-container { max-width: 1100px; margin: 0 auto; animation: fadeIn 0.4s ease; }
  .stats-row { display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1.5rem; margin-bottom: 2.5rem; }
  .panel-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(300px, 1fr)); gap: 2rem; }
  
  .card { 
    background: var(--card-bg); border: 1px solid var(--border); border-radius: var(--radius-lg); 
    padding: 2rem; margin-bottom: 1.5rem; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1); 
  }
  .card-title { font-size: 1.1rem; font-weight: 600; margin-bottom: 1.5rem; color: var(--text-main); display: flex; align-items: center; gap: 0.75rem; }

  /* Navigation */
  .nav-group { display: flex; flex-direction: column; gap: 0.5rem; margin-top: 2rem; }
  .nav-item {
    display: flex; align-items: center; gap: 14px; padding: 12px 16px; border-radius: var(--radius-md);
    color: var(--text-muted); cursor: pointer; transition: 0.3s; font-weight: 500;
  }
  .nav-item:hover { background: rgba(255,255,255,0.05); color: white; }
  .nav-item.active { background: rgba(99, 102, 241, 0.1); color: var(--primary); border: 1px solid rgba(99, 102, 241, 0.2); }

  .nav-logout { margin-top: auto; color: var(--danger); font-weight: 600; }
  .nav-logout:hover { background: rgba(239, 68, 68, 0.1); }

  /* Buttons & Inputs */
  .btn-primary { 
    background: var(--primary); color: white; border: none; border-radius: 8px; 
    padding: 0.85rem 1.5rem; font-weight: 600; cursor: pointer; transition: 0.2s;
    display: flex; align-items: center; justify-content: center; gap: 0.5rem; width: 100%;
  }
  .btn-primary:hover { background: var(--primary-hover); transform: translateY(-1px); }
  
  .form-input, .form-textarea { 
    background: #111827; border: 1px solid var(--border); border-radius: 8px; 
    color: white; padding: 0.85rem 1rem; width: 100%; outline: none; margin-bottom: 1rem;
  }
  .form-label { font-size: 0.85rem; color: var(--text-muted); margin-bottom: 8px; display: block; font-weight: 500; }

  @keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
`;

const DashboardLayout = ({ currentTab, setCurrentTab, showLogoutModal, setShowLogoutModal, handleLogout, children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  return (
    <TokenGuard>
      <style>{dashboardStyles}</style>
      <div className="dashboard-layout">
        <header className="mobile-header">
           <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "var(--primary)", display: "grid", placeItems: "center", fontWeight: "bold" }}>M</div>
              <span style={{ fontWeight: 800 }}>Multiphasic</span>
           </div>
           <button onClick={() => setIsSidebarOpen(!isSidebarOpen)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>
             <svg width="28" height="28" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="3" y1="12" x2="21" y2="12"></line><line x1="3" y1="6" x2="21" y2="6"></line><line x1="3" y1="18" x2="21" y2="18"></line></svg>
           </button>
        </header>

        <aside className={`sidebar ${isSidebarOpen ? 'open' : ''}`}>
          <div className="logo-area" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2rem' }}>
            <svg width="220" height="60" viewBox="0 0 300 80" fill="none" xmlns="http://www.w3.org/2000/svg">
              <g transform="translate(0, 10)">
                <path d="M10 50 L 25 10 L 40 35 L 55 5 L 70 40" stroke="var(--primary)" strokeWidth="6" strokeLinecap="round" strokeLinejoin="round"/>
                <circle cx="25" cy="-5" r="4" fill="var(--success)"/>
                <circle cx="55" cy="-10" r="4" fill="var(--warning)"/>
              </g>
              <text x="90" y="35" fontFamily="Inter, sans-serif" fontSize="22" fontWeight="800" fill="var(--text-main)">Multiphasic</text>
              <text x="90" y="58" fontFamily="Inter, sans-serif" fontSize="14" fontWeight="500" fill="var(--text-muted)">Infraconstruct</text>
            </svg>
          </div>

          <nav className="nav-group">
            <div className={`nav-item ${currentTab === "overview" ? "active" : ""}`} onClick={() => { setCurrentTab("overview"); setIsSidebarOpen(false); }}>Overview</div>
            <div className={`nav-item ${currentTab === "messaging" ? "active" : ""}`} onClick={() => { setCurrentTab("messaging"); setIsSidebarOpen(false); }}>Campaigns</div>
            <div className={`nav-item ${currentTab === "contacts" ? "active" : ""}`} onClick={() => { setCurrentTab("contacts"); setIsSidebarOpen(false); }}>Contacts</div>
            <div className={`nav-item ${currentTab === "security" ? "active" : ""}`} onClick={() => { setCurrentTab("security"); setIsSidebarOpen(false); }}>Security</div>
            <div className={`nav-item ${currentTab === "docs" ? "active" : ""}`} onClick={() => { setCurrentTab("docs"); setIsSidebarOpen(false); }}>API Docs</div>
          </nav>

          <div className="nav-item nav-logout" onClick={() => setShowLogoutModal(true)}>Clear Session</div>
        </aside>

        <main className="main-content">
          {children}
        </main>

        {showLogoutModal && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(10px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
            <div className="card" style={{ maxWidth: '400px', width: '100%', textAlign: 'center', padding: '2.5rem' }}>
              <h2 style={{ marginBottom: '1rem' }}>Confirm Logout</h2>
              <p style={{ color: 'var(--text-muted)', marginBottom: '2rem' }}>Are you sure you want to end your session?</p>
              <div style={{ display: 'flex', gap: '1rem' }}>
                <button className="btn-primary" style={{ background: 'var(--border)', flex: 1 }} onClick={() => setShowLogoutModal(false)}>Cancel</button>
                <button className="btn-primary" style={{ background: 'var(--danger)', flex: 1 }} onClick={handleLogout}>Log Out</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </TokenGuard>
  );
};

export default DashboardLayout;